/**
 * Emoji data, lifted from the original picker.
 *
 * The grid is generated from Unicode ranges rather than shipped as a data
 * file, so the bundle stays small; only the keywords for the emoji people
 * actually reach for are spelled out.
 */

/** [label, [ [start, end], ... ] ] - inclusive codepoint ranges. */
/*
 * The standard emoji set, by category.
 *
 * The blocks Apple and every other platform actually ship, rather than every
 * codepoint Unicode has ever reserved - casting the net wider only filled the
 * picker with variants and regional oddities nobody reaches for. Anything the
 * platform still cannot draw is dropped in emojiSet().
 *
 * Each category carries the icon its tab shows, because a row of words takes
 * more room than the grid it labels.
 */
export const CATEGORIES: [string, [number, number][], string][] = [
  ['Smileys', [[0x1F600, 0x1F64F], [0x1F910, 0x1F92F], [0x1F970, 0x1F97A]], 'smile'],
  ['People', [[0x1F464, 0x1F487], [0x1F930, 0x1F93A], [0x1F9B0, 0x1F9DD]], 'profile'],
  ['Gestures', [[0x1F440, 0x1F450], [0x1F590, 0x1F596], [0x1F918, 0x1F91F]], 'handshake'],
  ['Animals', [[0x1F400, 0x1F43F], [0x1F980, 0x1F9AE]], 'heart'],
  ['Food', [[0x1F32D, 0x1F37F], [0x1F950, 0x1F96F]], 'fire'],
  ['Activity', [[0x1F3A0, 0x1F3CA], [0x1F3CF, 0x1F3F0], [0x1F93C, 0x1F945]], 'trophy'],
  ['Travel', [[0x1F680, 0x1F6A4], [0x1F6B2, 0x1F6C5], [0x1F30D, 0x1F320]], 'globe'],
  ['Objects', [[0x1F4A0, 0x1F4FF], [0x1F5A5, 0x1F5A8], [0x1F9E0, 0x1F9FF]], 'layers'],
  ['Symbols', [[0x2600, 0x26FF], [0x2700, 0x27BF], [0x1F500, 0x1F53D]], 'sparkle']
];

/**
 * Every national flag.
 *
 * Flags are not a codepoint range - each one is a pair of regional indicator
 * letters - so they are built from the ISO country codes rather than swept
 * like the other categories. Searching by country name works because the
 * name goes in as the keyword.
 */
export const COUNTRIES: Record<string, string> = {
  AD: 'Andorra', AE: 'United Arab Emirates', AF: 'Afghanistan', AG: 'Antigua and Barbuda',
  AI: 'Anguilla', AL: 'Albania', AM: 'Armenia', AO: 'Angola', AQ: 'Antarctica', AR: 'Argentina',
  AS: 'American Samoa', AT: 'Austria', AU: 'Australia', AW: 'Aruba', AX: 'Aland Islands',
  AZ: 'Azerbaijan', BA: 'Bosnia and Herzegovina', BB: 'Barbados', BD: 'Bangladesh',
  BE: 'Belgium', BF: 'Burkina Faso', BG: 'Bulgaria', BH: 'Bahrain', BI: 'Burundi', BJ: 'Benin',
  BL: 'Saint Barthelemy', BM: 'Bermuda', BN: 'Brunei', BO: 'Bolivia', BR: 'Brazil',
  BS: 'Bahamas', BT: 'Bhutan', BW: 'Botswana', BY: 'Belarus', BZ: 'Belize', CA: 'Canada',
  CD: 'Congo Kinshasa', CF: 'Central African Republic', CG: 'Congo Brazzaville',
  CH: 'Switzerland', CI: 'Cote dIvoire', CK: 'Cook Islands', CL: 'Chile', CM: 'Cameroon',
  CN: 'China', CO: 'Colombia', CR: 'Costa Rica', CU: 'Cuba', CV: 'Cape Verde',
  CW: 'Curacao', CY: 'Cyprus', CZ: 'Czechia', DE: 'Germany', DJ: 'Djibouti', DK: 'Denmark',
  DM: 'Dominica', DO: 'Dominican Republic', DZ: 'Algeria', EC: 'Ecuador', EE: 'Estonia',
  EG: 'Egypt', EH: 'Western Sahara', ER: 'Eritrea', ES: 'Spain', ET: 'Ethiopia', EU: 'European Union',
  FI: 'Finland', FJ: 'Fiji', FK: 'Falkland Islands', FM: 'Micronesia', FO: 'Faroe Islands',
  FR: 'France', GA: 'Gabon', GB: 'United Kingdom', GD: 'Grenada', GE: 'Georgia',
  GG: 'Guernsey', GH: 'Ghana', GI: 'Gibraltar', GL: 'Greenland', GM: 'Gambia', GN: 'Guinea',
  GQ: 'Equatorial Guinea', GR: 'Greece', GT: 'Guatemala', GU: 'Guam', GW: 'Guinea Bissau',
  GY: 'Guyana', HK: 'Hong Kong', HN: 'Honduras', HR: 'Croatia', HT: 'Haiti', HU: 'Hungary',
  ID: 'Indonesia', IE: 'Ireland', IL: 'Israel', IM: 'Isle of Man', IN: 'India', IQ: 'Iraq',
  IR: 'Iran', IS: 'Iceland', IT: 'Italy', JE: 'Jersey', JM: 'Jamaica', JO: 'Jordan',
  JP: 'Japan', KE: 'Kenya', KG: 'Kyrgyzstan', KH: 'Cambodia', KI: 'Kiribati', KM: 'Comoros',
  KN: 'Saint Kitts and Nevis', KP: 'North Korea', KR: 'South Korea', KW: 'Kuwait',
  KY: 'Cayman Islands', KZ: 'Kazakhstan', LA: 'Laos', LB: 'Lebanon', LC: 'Saint Lucia',
  LI: 'Liechtenstein', LK: 'Sri Lanka', LR: 'Liberia', LS: 'Lesotho', LT: 'Lithuania',
  LU: 'Luxembourg', LV: 'Latvia', LY: 'Libya', MA: 'Morocco', MC: 'Monaco', MD: 'Moldova',
  ME: 'Montenegro', MF: 'Saint Martin', MG: 'Madagascar', MH: 'Marshall Islands',
  MK: 'North Macedonia', ML: 'Mali', MM: 'Myanmar', MN: 'Mongolia', MO: 'Macao',
  MP: 'Northern Mariana Islands', MQ: 'Martinique', MR: 'Mauritania', MS: 'Montserrat',
  MT: 'Malta', MU: 'Mauritius', MV: 'Maldives', MW: 'Malawi', MX: 'Mexico', MY: 'Malaysia',
  MZ: 'Mozambique', NA: 'Namibia', NC: 'New Caledonia', NE: 'Niger', NF: 'Norfolk Island',
  NG: 'Nigeria', NI: 'Nicaragua', NL: 'Netherlands', NO: 'Norway', NP: 'Nepal', NR: 'Nauru',
  NU: 'Niue', NZ: 'New Zealand', OM: 'Oman', PA: 'Panama', PE: 'Peru', PF: 'French Polynesia',
  PG: 'Papua New Guinea', PH: 'Philippines', PK: 'Pakistan', PL: 'Poland',
  PM: 'Saint Pierre and Miquelon', PN: 'Pitcairn Islands', PR: 'Puerto Rico', PS: 'Palestine',
  PT: 'Portugal', PW: 'Palau', PY: 'Paraguay', QA: 'Qatar', RE: 'Reunion', RO: 'Romania',
  RS: 'Serbia', RU: 'Russia', RW: 'Rwanda', SA: 'Saudi Arabia', SB: 'Solomon Islands',
  SC: 'Seychelles', SD: 'Sudan', SE: 'Sweden', SG: 'Singapore', SH: 'Saint Helena',
  SI: 'Slovenia', SJ: 'Svalbard and Jan Mayen', SK: 'Slovakia', SL: 'Sierra Leone',
  SM: 'San Marino', SN: 'Senegal', SO: 'Somalia', SR: 'Suriname', SS: 'South Sudan',
  ST: 'Sao Tome and Principe', SV: 'El Salvador', SX: 'Sint Maarten', SY: 'Syria',
  SZ: 'Eswatini', TC: 'Turks and Caicos Islands', TD: 'Chad', TG: 'Togo', TH: 'Thailand',
  TJ: 'Tajikistan', TK: 'Tokelau', TL: 'Timor Leste', TM: 'Turkmenistan', TN: 'Tunisia',
  TO: 'Tonga', TR: 'Turkiye', TT: 'Trinidad and Tobago', TV: 'Tuvalu', TW: 'Taiwan',
  TZ: 'Tanzania', UA: 'Ukraine', UG: 'Uganda', US: 'United States', UY: 'Uruguay',
  UZ: 'Uzbekistan', VA: 'Vatican City', VC: 'Saint Vincent and the Grenadines',
  VE: 'Venezuela', VG: 'British Virgin Islands', VI: 'US Virgin Islands', VN: 'Vietnam',
  VU: 'Vanuatu', WF: 'Wallis and Futuna', WS: 'Samoa', XK: 'Kosovo', YE: 'Yemen',
  YT: 'Mayotte', ZA: 'South Africa', ZM: 'Zambia', ZW: 'Zimbabwe'
};

/** Two letters to the flag they spell in regional indicators. */
export function flagOf(code: string): string {
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map((c) => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
}

export const KEYWORDS: Record<string, string> = {
  '😀': 'grin smile happy', '😃': 'smile happy', '😄': 'laugh happy smile', '😁': 'beam grin',
  '😂': 'lol laugh cry tears funny', '🤣': 'rofl laugh rolling funny', '😊': 'blush smile',
  '😉': 'wink', '😍': 'love heart eyes', '🥰': 'love hearts adore', '😘': 'kiss',
  '😎': 'cool sunglasses', '🤩': 'star struck wow', '🤔': 'think hmm', '😐': 'neutral meh',
  '🙄': 'eyeroll annoyed', '😴': 'sleep tired', '😭': 'cry sad sob', '😤': 'angry steam mad',
  '😡': 'angry mad rage', '🤬': 'swear angry curse', '😱': 'scream shock fear', '🥶': 'cold freeze',
  '🥵': 'hot heat', '🤮': 'sick vomit', '💀': 'skull dead dying rip', '👻': 'ghost boo',
  '🤖': 'robot bot', '👽': 'alien', '🤡': 'clown', '💩': 'poop crap',
  '👍': 'thumbs up yes good agree ok', '👎': 'thumbs down no bad disagree',
  '👌': 'ok perfect', '🤝': 'handshake deal', '👏': 'clap applause', '🙏': 'pray thanks please',
  '💪': 'muscle strong flex', '🖐': 'hand stop', '✌': 'peace victory', '🤞': 'fingers crossed luck',
  '🫡': 'salute respect o7', '🫠': 'melting', '🤙': 'call shaka',
  '🔥': 'fire lit hot burn', '💯': 'hundred perfect', '✨': 'sparkles shiny', '⭐': 'star',
  '⚡': 'lightning fast bolt', '💥': 'boom explosion', '❤': 'heart love red',
  '💔': 'broken heart', '🎉': 'party celebrate tada', '🎊': 'confetti party',
  '🏆': 'trophy win first', '🥇': 'gold first place win', '🎯': 'target bullseye aim',
  '🎮': 'game controller gaming play', '🕹': 'joystick arcade', '🎲': 'dice random',
  '💰': 'money cash rich', '💎': 'diamond gem', '👑': 'crown king queen',
  '🚀': 'rocket launch fast', '🐐': 'goat greatest', '🧠': 'brain smart galaxy',
  '👀': 'eyes look watch sus', '🫥': 'invisible', '🗿': 'moai stone',
  '✅': 'check yes done tick', '❌': 'cross no wrong', '⚠': 'warning caution',
  '🔇': 'mute muted', '🔊': 'sound loud volume', '🎧': 'headphones audio',
  '🎤': 'mic microphone', '📷': 'camera photo', '💻': 'laptop pc computer',
  '🖥': 'monitor screen pc', '⌨': 'keyboard', '🖱': 'mouse',
  '😈': 'devil evil mischief', '🤗': 'hug', '🤠': 'cowboy', '🥳': 'party face celebrate',
  '😅': 'sweat nervous laugh', '😬': 'grimace awkward', '🙃': 'upside down',
  '🫶': 'heart hands love', '🙌': 'raise hands praise', '🤷': 'shrug idk',
  '🏳': 'flag surrender', '⏰': 'clock time alarm', '📌': 'pin', '🔗': 'link chain'};

export const FREQUENT = ['👍', '😂', '🔥', '💀', '❤', '🎉', '👀', '😭', '💯', '🤝',
  '🎮', '🏆', '⚡', '😎', '🤔', '👏', '🙏', '✅', '❌', '🫡'];
